import {gql} from '@apollo/client';
import stringify from 'json-stable-stringify';

import {assert} from 'Asserts';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetContainerSchemaTags,
  GetContainerSchemaTagsVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetContainerSchemaTags';
import {
  GetWellTagsPlate,
  stubbableFunctions,
} from 'notebook/integrations/samples/transfer-table-columns/DestinationPlateCustomEval';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {SchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  CustomResolutionQueryType,
  InventoryTableSchemaTagsFromContainerQuery,
  InventoryTableSchemaTagsFromPlateWellQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {CustomEval, CustomEvalResult} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  convertTagToTypedValue,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import {findSchemaTagByApiId} from 'schemas/tags/TagUtil';
import APIUtil from 'util/APIUtil';
import lruMemoize from 'util/lruMemoize';

const GET_CONTAINER_SCHEMA_TAGS = gql`
  query GetContainerSchemaTags($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        tagsJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function autopopulateContainerSchemaTags(
  query: InventoryTableSchemaTagsFromContainerQuery
): Promise<ServerResolutionResult> {
  const {containerId, tagSchemaFieldApiId} = query;
  const data = await doQueryWithoutCache<GetContainerSchemaTags, GetContainerSchemaTagsVariables>(
    {query: GET_CONTAINER_SCHEMA_TAGS},
    {variables: {containerIds: [containerId]}}
  );
  const container = data.containers[0];
  if (isInaccessible(container)) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }

  const schemaTag = findSchemaTagByApiId(container.tagsJson, tagSchemaFieldApiId);
  const typedValue = schemaTag ? convertTagToTypedValue(schemaTag) : toStringTypedValue('');

  return makeCustomResolutionResultFromTypedValue(typedValue);
}

export function containerSchemaTagCustomEval(
  typedValue: TypedValue,
  columnDefinition: SchemaFieldColumnDefinition,
  [containerTypedValue]: [TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (!isTypedValueEmpty(typedValue)) {
    return {val: typedValue, error: null, locked: false};
  }
  if (containerTypedValue == null || containerTypedValue.type !== BaseTypedValueType.STORABLES) {
    return {val: typedValue, error: null, locked: false};
  }

  const {id: containerId} = containerTypedValue.storableSummaries[0];
  if (!APIUtil.isContainer(containerId)) {
    return {val: typedValue, error: null, locked: false};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SCHEMA_TAGS_FROM_CONTAINER,
      containerId,
      tagSchemaFieldApiId: columnDefinition.tagSchemaFieldApiId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  return {val: resolvedValue, error, locked: false};
}

export async function autopopulatePlateWellTags(
  query: InventoryTableSchemaTagsFromPlateWellQuery
): Promise<ServerResolutionResult> {
  const {plateId, positionString, plateSchemaId, tagSchemaFieldApiId} = query;

  // Query for the plate's width and height.
  const plateSizeQueryData = await stubbableFunctions.doPlateSizeQuery(plateId);
  const plateWithSizeInfo = plateSizeQueryData.plates[0];
  if (isInaccessible(plateWithSizeInfo)) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }

  const storableSchema = plateWithSizeInfo.storableSchema;
  assert(storableSchema.__typename === 'PlateSchemaNode', 'Plate must have plate schema.');
  if (storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }
  if (plateSchemaId != null && storableSchema.id !== plateSchemaId) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }
  const {height, width} = storableSchema;

  // Convert the position string to row and column indices.
  let rowIndex;
  let columnIndex;
  try {
    ({rowIndex, columnIndex} = parseGridPosition({
      position: positionString,
      gridHeight: height,
      gridWidth: width,
    }));
  } catch (e) {
    if (e instanceof InvalidPositionError) {
      return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
    }
    throw e;
  }

  // Query for the schema tags of that plate well.
  const plateWellTagsData = await stubbableFunctions.doPlateWellTagsQuery(plateId, rowIndex, columnIndex);
  const plateWithTags = plateWellTagsData.directStorable;
  if (isInaccessible(plateWithTags)) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }

  const {cell} = plateWithTags as GetWellTagsPlate;
  assert(cell != null, 'Wells in a plate must not be null');
  const container = cell.container;
  if (isInaccessible(container)) {
    return makeCustomResolutionResultFromTypedValue(null);
  }
  const {tagsJson} = container;
  const schemaTag = findSchemaTagByApiId(tagsJson, tagSchemaFieldApiId);
  const typedValue = schemaTag ? convertTagToTypedValue(schemaTag) : toStringTypedValue('');

  return makeCustomResolutionResultFromTypedValue(typedValue);
}

export const getPlateWellSchemaTagCustomEval = lruMemoize(
  _getPlateWellSchemaTagCustomEval,
  {max: 100},
  (...args) => stringify(args)
);

/**
 * This function returns resolves schema tags for individual plate wells.
 */
function _getPlateWellSchemaTagCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: SchemaFieldColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(typedValue)) {
      return {val: typedValue, error: null, locked: false};
    }
    if (plateTypedValue == null || plateTypedValue.type !== BaseTypedValueType.STORABLES) {
      return {val: typedValue, error: null, locked: false};
    }
    if (
      positionTypedValue == null ||
      positionTypedValue.type !== BaseTypedValueType.STRING ||
      positionTypedValue.rawValue === ''
    ) {
      return {val: typedValue, error: null, locked: false};
    }

    const positionString = positionTypedValue.rawValue;
    const plateId = plateTypedValue.storableSummaries[0].id;
    if (!APIUtil.isPlate(plateId)) {
      return {val: typedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SCHEMA_TAGS_FROM_PLATE_WELL,
        plateId,
        positionString,
        ...(plateSchemaId != null && {plateSchemaId}),
        tagSchemaFieldApiId: columnDefinition.tagSchemaFieldApiId,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );

    return {val: resolvedValue, error, locked: false};
  };
}
